const HEX_SIX = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const HEX_EIGHT = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const INK_DARK = "#000000";
const INK_DARK_LUMINANCE = 0;
const INK_LIGHT = "#FFFFFF";
const INK_LIGHT_LUMINANCE = 1;
const CONTRAST_OFFSET = 0.05;
const SRGB_THRESHOLD = 0.03928;
const LINEAR_FACTOR = 12.92;
const GAMMA_BASE = 0.055;
const GAMMA_DIVISOR = 1.055;
const GAMMA = 2.4;
const WEIGHT_R = 0.2126;
const WEIGHT_G = 0.7152;
const WEIGHT_B = 0.0722;
const CHANNEL_MAX = 255;
const CHANNEL_GROUPS_START = 1;
const HEX_CHANNEL_COUNT = 3;
const HEX_EIGHT_CHANNEL_COUNT = 4;
const linearize = (channel) => channel <= SRGB_THRESHOLD ? channel / LINEAR_FACTOR : ((channel + GAMMA_BASE) / GAMMA_DIVISOR) ** GAMMA;
const srgbChannel = (channel) => parseInt(channel, 16) / CHANNEL_MAX;
const relativeLuminance = (hex) => {
    const match = HEX_SIX.exec(hex);
    if (!match) {
        return undefined;
    }
    const [red, green, blue] = match
        .slice(CHANNEL_GROUPS_START, CHANNEL_GROUPS_START + HEX_CHANNEL_COUNT)
        .map((channel) => linearize(srgbChannel(channel)));
    return WEIGHT_R * red + WEIGHT_G * green + WEIGHT_B * blue;
};
/** Alpha-composites an 8-digit `#rrggbbaa` swatch over the surface behind it, in sRGB space. */
const compositedLuminance = (backgroundHex, surfaceHex) => {
    const foreground = HEX_EIGHT.exec(backgroundHex);
    const surface = HEX_SIX.exec(surfaceHex);
    if (!foreground || !surface) {
        return undefined;
    }
    const alpha = srgbChannel(foreground[HEX_EIGHT_CHANNEL_COUNT] ?? "ff");
    const [red, green, blue] = foreground
        .slice(CHANNEL_GROUPS_START, CHANNEL_GROUPS_START + HEX_CHANNEL_COUNT)
        .map((channel, index) => linearize(alpha * srgbChannel(channel) + (1 - alpha) * srgbChannel(surface[CHANNEL_GROUPS_START + index] ?? "00")));
    return WEIGHT_R * red + WEIGHT_G * green + WEIGHT_B * blue;
};
/**
 * WCAG-readable label color for text drawn over a meter hex: dark ink on light swatches, white on dark ones.
 * For a translucent `#rrggbbaa` swatch, pass the `surfaceHex` it sits on; without it the alpha is unread
 * (the swatch fails the six-digit match) and the result falls back to white, matching unparseable input.
 */
export default function resolveReadableTextColor({ backgroundHex, surfaceHex }) {
    const luminance = HEX_EIGHT.test(backgroundHex) && surfaceHex !== undefined ? compositedLuminance(backgroundHex, surfaceHex) : relativeLuminance(backgroundHex);
    if (luminance === undefined) {
        return INK_LIGHT;
    }
    const darkRatio = (luminance + CONTRAST_OFFSET) / (INK_DARK_LUMINANCE + CONTRAST_OFFSET);
    const lightRatio = (INK_LIGHT_LUMINANCE + CONTRAST_OFFSET) / (luminance + CONTRAST_OFFSET);
    return darkRatio >= lightRatio ? INK_DARK : INK_LIGHT;
}
